import { useCallback, useEffect, useState } from 'react';

const CACHE_KEY = 'saloni_trending_products_v1';
const CACHE_TTL_MS = 5 * 60 * 1000;

function readTrendingCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    if (!Array.isArray(parsed.products)) return null;
    return parsed.products;
  } catch {
    return null;
  }
}

function writeTrendingCache(products) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), products }));
  } catch {
    /* quota / private mode */
  }
}

async function fetchTrendingProducts({ days = 30, limit = 12 } = {}) {
  const params = new URLSearchParams({
    days: String(days),
    limit: String(limit),
  });
  const r = await fetch(`/api/trending_products.php?${params}`, { cache: 'default' });
  const data = await r.json().catch(() => ({}));
  if (!data?.ok || !Array.isArray(data.products)) {
    return [];
  }
  return data.products;
}

/**
 * Most-viewed product picks for the home page (last 30 days by default).
 * @returns {{ picks: Array<{ category_slug: string, product_id: string }>, loading: boolean, refetch: Function }}
 */
export function useTrendingProducts({ days = 30, limit = 12, enabled = true } = {}) {
  const [picks, setPicks] = useState(() => (enabled ? readTrendingCache() : null) || []);
  const [loading, setLoading] = useState(enabled && picks.length === 0);

  const refetch = useCallback(async ({ silent = false } = {}) => {
    if (!enabled) return [];
    if (!silent) setLoading(true);
    try {
      const products = await fetchTrendingProducts({ days, limit });
      const normalized = products
        .map((p) => ({
          category_slug: String(p.category_slug || '').trim(),
          product_id: String(p.product_id || p.product_external_id || '').trim(),
        }))
        .filter((p) => p.category_slug && p.product_id);
      setPicks(normalized);
      writeTrendingCache(normalized);
      return normalized;
    } catch {
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  }, [days, enabled, limit]);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    (async () => {
      const cached = readTrendingCache();
      if (cached?.length) {
        setPicks(cached);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const products = await fetchTrendingProducts({ days, limit });
        if (cancelled) return;
        const normalized = products
          .map((p) => ({
            category_slug: String(p.category_slug || '').trim(),
            product_id: String(p.product_id || p.product_external_id || '').trim(),
          }))
          .filter((p) => p.category_slug && p.product_id);
        setPicks(normalized);
        writeTrendingCache(normalized);
      } catch {
        if (!cancelled) setPicks([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [days, enabled, limit]);

  return { picks, loading, refetch };
}
